import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Grid,
  FormHelperText
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { restaurantTableApi, restaurantTableGroupApi, RestaurantTableDto, RestaurantTableGroupDto } from '../../../../services/restaurantApiService';
import { useTranslation } from 'react-i18next';

interface RestaurantTablesTabProps {
  restaurantScreenId: number;
  companyId: string;
  onOpenManageGroupsModal?: () => void;
}

interface EditTableModalProps {
  open: boolean;
  onClose: () => void;
  table: RestaurantTableDto;
  groups: RestaurantTableGroupDto[];
  onTableUpdated: () => void;
  restaurantScreenId: number;
}

const EditTableModal: React.FC<EditTableModalProps> = ({
  open,
  onClose,
  table,
  groups,
  onTableUpdated,
  restaurantScreenId
}) => {
  const { t } = useTranslation('salesSystem');
  const [name, setName] = useState(table.Name);
  const [capacity, setCapacity] = useState<number | string>(table.Capacity);
  const [groupId, setGroupId] = useState<number | null>(table.TableGroupId || null);
  const [shape, setShape] = useState<'rectangle' | 'circle' | 'square'>(table.Shape as any || 'rectangle');
  const [notes, setNotes] = useState(table.Notes || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError(t('restaurantTablesTab.errors.tableNameRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await restaurantTableApi.update(table.Id, {
        Id: table.Id,
        Name: name.trim(),
        Capacity: Number(capacity),
        TableGroupId: groupId,
        Shape: shape,
        Notes: notes.trim()
      });
      console.log(`✅ Masa güncellendi: ${name}`);
      onTableUpdated();
      onClose();
    } catch (err: any) {
      console.error("❌ Masa güncellenirken hata:", err);
      setError(err.response?.data?.Message || err.message || t('restaurantTablesTab.errors.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('restaurantTablesTab.editModal.title')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          autoFocus
          label={t('restaurantTablesTab.editModal.tableName')}
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          margin="dense"
          required
          disabled={loading}
        />
        <TextField
          label={t('restaurantTablesTab.editModal.capacity')}
          type="number"
          fullWidth
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
          margin="dense"
          required
          disabled={loading}
          inputProps={{ min: "1" }}
        />
        <FormControl fullWidth margin="dense" disabled={loading}>
          <InputLabel>{t('restaurantTablesTab.editModal.tableGroup')}</InputLabel>
          <Select
            value={groupId || ''}
            label={t('restaurantTablesTab.editModal.tableGroup')}
            onChange={(e) => setGroupId(e.target.value ? Number(e.target.value) : null)}
          >
            <MenuItem value="">{t('restaurantTablesTab.editModal.groupNotSelected')}</MenuItem>
            {groups.map((group) => (
              <MenuItem key={group.Id} value={group.Id}>
                {group.Name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="dense" disabled={loading}>
          <InputLabel>{t('restaurantTablesTab.editModal.tableShape')}</InputLabel>
          <Select
            value={shape}
            label={t('restaurantTablesTab.editModal.tableShape')}
            onChange={(e) => setShape(e.target.value as any)}
          >
            <MenuItem value="rectangle">{t('restaurantTablesTab.shapes.rectangle')}</MenuItem>
            <MenuItem value="circle">{t('restaurantTablesTab.shapes.circle')}</MenuItem>
            <MenuItem value="square">{t('restaurantTablesTab.shapes.square')}</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label={t('restaurantTablesTab.editModal.notes')}
          fullWidth
          multiline
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          margin="dense"
          disabled={loading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>{t('restaurantTablesTab.common.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !name.trim()}>
          {loading ? <CircularProgress size={24} /> : t('restaurantTablesTab.common.update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RestaurantTablesTab: React.FC<RestaurantTablesTabProps> = ({
  restaurantScreenId,
  companyId,
  onOpenManageGroupsModal
}) => {
  const { t } = useTranslation('salesSystem');
  const [tables, setTables] = useState<(RestaurantTableDto & { groupName?: string })[]>([]);
  const [groups, setGroups] = useState<RestaurantTableGroupDto[]>([]);
  const [loadingTables, setLoadingTables] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newTableName, setNewTableName] = useState('');
  const [newCapacity, setNewCapacity] = useState<number | string>('');
  const [newSelectedGroupId, setNewSelectedGroupId] = useState<number | null>(null);
  const [newNotes, setNewNotes] = useState('');
  const [newShape, setNewShape] = useState<'rectangle' | 'circle' | 'square'>('rectangle');
  const [newUseCustomCapacity, setNewUseCustomCapacity] = useState(true);
  const [addingTable, setAddingTable] = useState(false);
  const [addTableError, setAddTableError] = useState<string | null>(null);

  const [isBulkAddMode, setIsBulkAddMode] = useState(false);
  const [bulkAddInput, setBulkAddInput] = useState('');
  const [bulkAddError, setBulkAddError] = useState<string | null>(null);

  const [isEditTableModalOpen, setIsEditTableModalOpen] = useState(false);
  const [tableToEdit, setTableToEdit] = useState<RestaurantTableDto | null>(null);
  const [tableToDelete, setTableToDelete] = useState<RestaurantTableDto | null>(null);
  const [isDeleteTableConfirmOpen, setIsDeleteTableConfirmOpen] = useState(false);
  const [deletingTable, setDeletingTable] = useState(false);

  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setError(null);
    setAddTableError(null);
  };

  const fetchTables = async () => {
    if (!restaurantScreenId) return;
    setLoadingTables(true);
    setError(null);
    try {
      const [tablesData, groupsData] = await Promise.all([
        restaurantTableApi.getByRestaurantScreenId(restaurantScreenId),
        restaurantTableGroupApi.getByRestaurantScreenId(restaurantScreenId)
      ]);

      const enrichedTables = tablesData.map(table => ({
        ...table,
        groupName: table.TableGroupId
          ? groupsData.find(g => g.Id === table.TableGroupId)?.Name || t('restaurantTablesTab.unknownGroup')
          : '-'
      }));

      setTables(enrichedTables.sort((a, b) => a.Name.localeCompare(b.Name)));
      setGroups(groupsData);
    } catch (err) {
      console.error("❌ Masalar yüklenirken hata:", err);
      setError(t('restaurantTablesTab.errors.loadFailed'));
    } finally {
      setLoadingTables(false);
    }
  };

  useEffect(() => {
    fetchTables();
  }, [restaurantScreenId]);

  const handleNewCapacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^[0-9]+$/.test(value)) {
      setNewCapacity(value);
    }
  };

  const resetAddForm = () => {
    setNewTableName('');
    setNewCapacity('');
    setNewSelectedGroupId(null);
    setNewNotes('');
    setNewShape('rectangle');
    setNewUseCustomCapacity(true);
    setAddTableError(null);
    setBulkAddInput('');
    setBulkAddError(null);
  };

  const handleAddTableSubmit = async () => {
    setAddingTable(true);
    setAddTableError(null);
    setBulkAddError(null);

    if (!newSelectedGroupId) {
      const errorMsg = t('restaurantTablesTab.errors.groupRequired');
      if (isBulkAddMode) {
        setBulkAddError(errorMsg);
      } else {
        setAddTableError(errorMsg);
      }
      setAddingTable(false);
      return;
    }

    try {
      if (isBulkAddMode) {
        const pattern = /^(.*?)\s*(\d+)-(\d+)$/;
        const match = bulkAddInput.trim().match(pattern);

        if (!match) {
          setBulkAddError(t('restaurantTablesTab.errors.invalidBulkFormat'));
          setAddingTable(false);
          return;
        }

        const prefix = match[1].trim() || 'Masa';
        const startNum = parseInt(match[2], 10);
        const endNum = parseInt(match[3], 10);

        if (startNum > endNum) {
          setBulkAddError(t('restaurantTablesTab.errors.invalidRange'));
          setAddingTable(false);
          return;
        }
        if (endNum - startNum + 1 > 50) {
          setBulkAddError(t('restaurantTablesTab.errors.maxBulkLimit'));
          setAddingTable(false);
          return;
        }

        // Toplu ekleme: Loop ile
        for (let i = startNum; i <= endNum; i++) {
          const tableName = `${prefix} ${i}`;
          await restaurantTableApi.create({
            RestaurantScreenId: restaurantScreenId,
            Name: tableName,
            Capacity: newUseCustomCapacity ? Number(newCapacity) : 4,
            TableGroupId: newSelectedGroupId,
            Notes: newNotes.trim(),
            Shape: newShape
          });
        }
        console.log(`✅ ${endNum - startNum + 1} masa toplu olarak eklendi`);
        resetAddForm();
        await fetchTables();
      } else {
        if (!newTableName.trim()) {
          setAddTableError(t('restaurantTablesTab.errors.tableNameRequired'));
          setAddingTable(false);
          return;
        }
        if (newUseCustomCapacity && (newCapacity === '' || Number(newCapacity) <= 0)) {
          setAddTableError(t('restaurantTablesTab.errors.validCapacityRequired'));
          setAddingTable(false);
          return;
        }

        await restaurantTableApi.create({
          RestaurantScreenId: restaurantScreenId,
          Name: newTableName.trim(),
          Capacity: newUseCustomCapacity ? Number(newCapacity) : 4,
          TableGroupId: newSelectedGroupId,
          Notes: newNotes.trim(),
          Shape: newShape
        });
        console.log(`✅ Yeni masa eklendi: ${newTableName}`);
        resetAddForm();
        await fetchTables();
      }
    } catch (err: any) {
      console.error("❌ Masa eklenirken hata:", err);
      const errorMessage = err.response?.data?.Message || err.message || t('restaurantTablesTab.errors.addFailed');
      if (isBulkAddMode) {
        setBulkAddError(errorMessage);
      } else {
        setAddTableError(errorMessage);
      }
    } finally {
      setAddingTable(false);
    }
  };

  const handleOpenEditTableModal = (table: RestaurantTableDto) => {
    setTableToEdit(table);
    setIsEditTableModalOpen(true);
  };

  const handleCloseEditTableModal = () => {
    setIsEditTableModalOpen(false);
    setTableToEdit(null);
  };

  const handleTableUpdated = async () => {
    await fetchTables();
  };

  const handleOpenDeleteTableConfirm = (table: RestaurantTableDto) => {
    setTableToDelete(table);
    setIsDeleteTableConfirmOpen(true);
  };

  const handleCloseDeleteTableConfirm = () => {
    setTableToDelete(null);
    setIsDeleteTableConfirmOpen(false);
  };

  const handleDeleteTable = async () => {
    if (!tableToDelete) return;

    setDeletingTable(true);
    try {
      await restaurantTableApi.delete(tableToDelete.Id);
      console.log(`✅ Masa silindi: ${tableToDelete.Name}`);
      await fetchTables();
      handleCloseDeleteTableConfirm();
    } catch (err: any) {
      console.error("❌ Masa silinirken hata:", err);
      setError(err.response?.data?.Message || err.message || t('restaurantTablesTab.errors.deleteFailed'));
    } finally {
      setDeletingTable(false);
    }
  };

  return (
    <Box sx={{ p: 0, maxHeight: 500, overflowY: 'auto' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', position: 'sticky', top: 0, zIndex: 1 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label={t('restaurantTablesTab.tabs.addTable')} />
          <Tab label={t('restaurantTablesTab.tabs.manageTables')} />
        </Tabs>
      </Box>

      {currentTab === 0 && (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('restaurantTablesTab.addSection.title')}</Typography>
            <FormControlLabel
              control={<Switch checked={isBulkAddMode} onChange={(e) => setIsBulkAddMode(e.target.checked)} size="small" />}
              label={<Typography variant="body2">{t('restaurantTablesTab.addSection.bulkAdd')}</Typography>}
              sx={{ mr: 0 }}
            />
          </Box>

          {addTableError && !isBulkAddMode && <Alert severity="error" sx={{ mb: 1.5 }}>{addTableError}</Alert>}
          {bulkAddError && isBulkAddMode && <Alert severity="error" sx={{ mb: 1.5 }}>{bulkAddError}</Alert>}

          {isBulkAddMode ? (
            <>
              <TextField
                autoFocus
                label={t('restaurantTablesTab.addSection.bulkTableName')}
                fullWidth
                value={bulkAddInput}
                onChange={(e) => setBulkAddInput(e.target.value)}
                margin="dense"
                required
                disabled={addingTable}
                size="small"
                sx={{ mb: 0.5 }}
              />
              <FormHelperText sx={{ mb: 1.5, ml: 1.8 }}>
                {t('restaurantTablesTab.addSection.bulkFormatHint')}
              </FormHelperText>
            </>
          ) : (
            <TextField
              autoFocus
              label={t('restaurantTablesTab.addSection.tableNameOrNumber')}
              fullWidth
              value={newTableName}
              onChange={(e) => setNewTableName(e.target.value)}
              margin="dense"
              required
              disabled={addingTable}
              size="small"
              sx={{ mb: 1.5 }}
            />
          )}

          <Grid container spacing={1.5} sx={{ mb: 0.5 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newUseCustomCapacity}
                    onChange={(e) => setNewUseCustomCapacity(e.target.checked)}
                    disabled={addingTable}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t('restaurantTablesTab.addSection.customCapacity')}</Typography>}
                sx={{ mt: 0, mb: newUseCustomCapacity ? 0 : 1.5, width: '100%' }}
              />
            </Grid>

            {newUseCustomCapacity && (
              <Grid item xs={6}>
                <TextField
                  label={t('restaurantTablesTab.addSection.capacity')}
                  type="number"
                  fullWidth
                  value={newCapacity}
                  onChange={handleNewCapacityChange}
                  margin="dense"
                  required
                  disabled={addingTable}
                  inputProps={{ min: "1" }}
                  size="small"
                />
              </Grid>
            )}
            <Grid item xs={newUseCustomCapacity ? 6 : 12}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={onOpenManageGroupsModal ? 9 : 12}>
                  <FormControl
                    fullWidth
                    disabled={addingTable}
                    size="small"
                    margin="dense"
                    required
                    error={!newSelectedGroupId && (!!addTableError?.includes('grubu') || !!bulkAddError?.includes('grubu'))}
                  >
                    <InputLabel>{t('restaurantTablesTab.addSection.tableGroupRequired')}</InputLabel>
                    <Select
                      value={newSelectedGroupId || ''}
                      label={t('restaurantTablesTab.addSection.tableGroupRequired')}
                      onChange={(e) => setNewSelectedGroupId(e.target.value ? Number(e.target.value) : null)}
                    >
                      {groups.length === 0 && (
                        <MenuItem value="" disabled>
                          {t('restaurantTablesTab.addSection.noGroupFound')}
                        </MenuItem>
                      )}
                      {groups.map((group) => (
                        <MenuItem key={group.Id} value={group.Id}>
                          {group.Name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {onOpenManageGroupsModal && (
                  <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%', pt: '4px' }}>
                    <Tooltip title={t('restaurantTablesTab.addSection.manageGroups')}>
                      <IconButton onClick={onOpenManageGroupsModal} color="primary" disabled={addingTable} size="medium">
                        <PlaylistAddIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>

          <Grid container spacing={1.5} alignItems="flex-start">
            <Grid item xs={6}>
              <FormControl fullWidth margin="dense" disabled={addingTable} size="small">
                <InputLabel>Masa Şekli (Opsiyonel)</InputLabel>
                <Select
                  value={newShape}
                  label={t('restaurantTablesTab.addSection.tableShapeOptional')}
                  onChange={(e) => setNewShape(e.target.value as any)}
                >
                  <MenuItem value="rectangle">{t('restaurantTablesTab.shapes.rectangle')}</MenuItem>
                  <MenuItem value="circle">{t('restaurantTablesTab.shapes.circle')}</MenuItem>
                  <MenuItem value="square">{t('restaurantTablesTab.shapes.square')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t('restaurantTablesTab.addSection.notesOptional')}
                fullWidth
                multiline
                rows={2}
                value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
                margin="dense"
                disabled={addingTable}
                size="small"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={resetAddForm} sx={{ mr: 1 }} disabled={addingTable} size="medium">
              {t('restaurantTablesTab.common.clear')}
            </Button>
            <Button variant="contained" onClick={handleAddTableSubmit} disabled={addingTable} size="medium">
              {addingTable ? <CircularProgress size={24} /> : (isBulkAddMode ? t('restaurantTablesTab.common.bulkAdd') : t('restaurantTablesTab.common.addTable'))}
            </Button>
          </Box>
        </Box>
      )}

      {currentTab === 1 && (
        <Box sx={{ p: 0 }}>
          {loadingTables && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px" p={3}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>{t('restaurantTablesTab.loading')}</Typography>
            </Box>
          )}

          {!loadingTables && error && (
            <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>
          )}

          {!loadingTables && !error && tables.length === 0 && (
            <Paper sx={{ p: 3, textAlign: 'center', m: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('restaurantTablesTab.empty.title')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('restaurantTablesTab.empty.description')}
              </Typography>
            </Paper>
          )}

          {!loadingTables && !error && tables.length > 0 && (
            <Paper elevation={0} sx={{ borderRadius: 0, m: 0 }}>
              <TableContainer sx={{ maxHeight: 450, overflowY: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: 'grey.100' } }}>
                      <TableCell>{t('restaurantTablesTab.table.tableName')}</TableCell>
                      <TableCell align="center">{t('restaurantTablesTab.table.capacity')}</TableCell>
                      <TableCell>{t('restaurantTablesTab.table.group')}</TableCell>
                      <TableCell>{t('restaurantTablesTab.table.notes')}</TableCell>
                      <TableCell align="center">{t('restaurantTablesTab.table.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tables.map((table) => (
                      <TableRow
                        key={table.Id}
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell component="th" scope="row">
                          <Typography variant="body2" fontWeight="medium">{table.Name}</Typography>
                        </TableCell>
                        <TableCell align="center">{table.Capacity}</TableCell>
                        <TableCell>{table.groupName || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={table.Notes || ''}>
                          {table.Notes || '-'}
                        </TableCell>
                        <TableCell align="center" sx={{ p: '4px' }}>
                          <Tooltip title={t('restaurantTablesTab.common.edit')}>
                            <span>
                              <IconButton size="small" onClick={() => handleOpenEditTableModal(table)} sx={{ mr: 0.5 }} disabled={deletingTable}>
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t('restaurantTablesTab.common.delete')}>
                            <span>
                              <IconButton size="small" onClick={() => handleOpenDeleteTableConfirm(table)} disabled={deletingTable} color="error">
                                <DeleteIcon fontSize="inherit" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      )}

      {tableToEdit && (
        <EditTableModal
          open={isEditTableModalOpen}
          onClose={handleCloseEditTableModal}
          table={tableToEdit}
          groups={groups}
          onTableUpdated={handleTableUpdated}
          restaurantScreenId={restaurantScreenId}
        />
      )}

      {tableToDelete && (
        <Dialog open={isDeleteTableConfirmOpen} onClose={handleCloseDeleteTableConfirm}>
          <DialogTitle>{t('restaurantTablesTab.deleteDialog.title')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('restaurantTablesTab.deleteDialog.description', { name: tableToDelete.Name })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteTableConfirm} disabled={deletingTable}>{t('restaurantTablesTab.common.cancel')}</Button>
            <Button onClick={handleDeleteTable} color="error" variant="contained" disabled={deletingTable}>
              {deletingTable ? <CircularProgress size={20} color="inherit" /> : t('restaurantTablesTab.common.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default RestaurantTablesTab;
